import React, { useCallback } from 'react';
import { notification, Select } from 'antd';
import { IOnSubmitArgs, OpForm } from 'components/customAntd/DLS/OpForm/OpForm';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store/store';
import { postRequest } from 'api/apiClient';
import { formatFullName } from 'utils/utils';
import { Visitor } from 'types/visitTypes';
import { OpModal } from 'components/customAntd/DLS/OpModal/OpModal';
import { fetchVisits } from 'store/slices/visitsSlice';
import { SIGNED_OUT_VISITOR_ID } from 'constants/userActivities';
import { DATE_TIME_FORMAT } from 'constants/dates';
import dayjs from 'dayjs';
import { OpFormItem } from 'components/customAntd/DLS/OpForm/formComponents/OpFormItem/OpFormItem';
import { searchVisitors, searchVisitorsToday } from 'store/slices/visitorSearchSlice';
import { useTranslation } from 'react-i18next';
import i18n from 'i18n/i18n';

const ReturnItemsModal: React.FC<{
    open: boolean;
    checkedVisitors: Visitor[];
    onClose: () => void;
    updateVisitorStatus: (visitorId: number) => void;
    setLoading: (loading: boolean) => void;
}> = ({ open, checkedVisitors, onClose, updateVisitorStatus, setLoading }) => {
    const dispatch: AppDispatch = useDispatch();
    const orgId = useSelector((state: RootState) => state.globalOrg.globalOrgId);
    const globalUserId = useSelector((state: RootState) => state.users.globalUser?.id);
    const { selectedVisit } = useSelector((state: RootState) => state.visits);
    const globalLocationId = useSelector((state: RootState) => state.locations.globalLocation?.id);
    const lastSearchParams = useSelector((state: RootState) => state.visitorSearch.lastSearchParams);

    const [form] = OpForm.useForm();
    const { t } = useTranslation();
    const handleOk = async () => {
        try {
            await form.validateFields();
            setLoading(true);
            onClose(); // Close modal immediately to show loading on drawer
            await form.submit();
        } catch (errorInfo) {
            notification.error({
                message: t('notification.error'),
                description: t('notification.validationError'),
                placement: 'bottomRight',
            });
        }
    };

    const handleSubmit = useCallback(async ({ values }: IOnSubmitArgs<any>) => {
        try {
            // update status of visit and visitors - parallel processing
            const visitorUpdatePromises = checkedVisitors.map(visitor =>
                visitor.id ? updateVisitorStatus(visitor.id) : Promise.resolve()
            );

            // Execute visitor updates and visitorExit in parallel
            await Promise.all([
                ...visitorUpdatePromises,
                postRequest(`/orgs/${orgId}/visitor/${selectedVisit?.id}/visitorExit?language=${i18n.language.split('-')[0]}`, {
                    signOut: dayjs().format(DATE_TIME_FORMAT),
                    confirmReturnItems: values.confirmReturnItems
                })
            ]);

            notification.success({
                message: t('notification.success'),
                description: t('visitors.returnItemsModal.notification.signOutSuccess'),
                placement: 'bottomRight',
            });

            // log activity and dispatch updates in parallel
            const fullNameList = checkedVisitors?.map((visitor: Visitor) => formatFullName(visitor.firstName, visitor.middleName, visitor.lastName,)).join(', ');

            const dispatchPromises = [
                postRequest(`/orgs/${orgId}/userActivity`, {
                    userId: globalUserId,
                    activityId: SIGNED_OUT_VISITOR_ID,
                    details: fullNameList
                }),
                dispatch(fetchVisits({ orgId })),
                dispatch(searchVisitorsToday({ orgId }))
            ];

            if (Object.keys(lastSearchParams).length !== 0) {
                dispatchPromises.push(
                    dispatch(searchVisitors({
                        ...lastSearchParams,
                        orgId,
                        siteId: lastSearchParams.siteId || globalLocationId!
                    }))
                );
            }

            await Promise.all(dispatchPromises);

            form.resetFields();

            console.log("Visitor updates completed.");
        } catch (error) {
            console.error("Sign out failed:", error);
            notification.error({
                message: t('notification.error'),
                description: t('visitors.returnItemsModal.notification.signOutFailed'),
                placement: 'bottomRight',
            });
        } finally {
            setLoading(false);
        }
    }, [orgId, selectedVisit, checkedVisitors, t, dispatch, form, globalUserId, globalLocationId, lastSearchParams, updateVisitorStatus, setLoading]);

    return (
        <OpModal
            open={open}
            title={t('visitors.returnItemsModal.title')}
            onOk={handleOk}
            onCancel={onClose}
            centered
        >
            <OpForm
                form={form}
                onSubmit={handleSubmit}
                defaultButtons={false}
                hasError={false}
                initialValues={{ confirmReturnItems: 1 }}
            >
                <OpFormItem
                    label={t('visitors.returnItemsModal.label')}
                    name="confirmReturnItems"
                    style={{ width: '100%' }}
                    rules={[{ required: true, message: t('visitors.returnItemsModal.required') }]}
                >
                    <Select
                        placeholder={t('visitors.returnItemsModal.placeholder')}
                        options={[
                            { label: t('visitors.returnItemsModal.yes'), value: 1 },
                            { label: t('visitors.returnItemsModal.no'), value: 0 }
                        ]}
                    />
                </OpFormItem>
            </OpForm>
        </OpModal>
    );
};

export default ReturnItemsModal;
